/**
 * Página pública de agendamento — a família chega aqui pelo site da escola.
 *
 * Diferença para o formulário antigo do Wix: aqui só aparecem horários que
 * estão realmente livres, e o agendamento cai direto no sistema, com a
 * confirmação saindo por WhatsApp na hora.
 *
 * Não usa o Layout do sistema de propósito: nada de menu, nada de login.
 * As fotos são espaços reservados — a marcação `FOTO:` diz o que entra em cada
 * um, e trocar é substituir o bloco por uma <img>.
 */
import { useEffect, useMemo, useState } from "react";
import { get, post } from "../lib/api";
import { LOGO } from "../lib/logo";
import { anoLetivoSugerido, sugerirEscolaridade } from "../lib/escolaridade";
import { dataBrParaIso, mascaraData, soDigitos } from "../lib/formato";
import { ehBrasil, mascaraPorPais, PAIS_PADRAO, PAISES, telefoneValido } from "../lib/paises";
import { ROTULO_PERIODO } from "../lib/tipos";

const VERDE = "#0e8c3a";
const VERDE_ESCURO = "#1a5c2e";

/**
 * ─── AS FOTOS DA PÁGINA ───────────────────────────────────────────────
 *
 * Cole o endereço de cada imagem entre as aspas. Enquanto ficar vazio,
 * aparece o espaço reservado cinza dizendo o que entra ali.
 *
 * Duas formas de conseguir o endereço:
 *
 * 1. Reaproveitar as do site: abra cocsjcsul.com.br, clique com o botão
 *    direito na foto → "Copiar endereço da imagem". Vem algo começando com
 *    https://static.wixstatic.com/... Cole aqui e pronto. Não precisa subir
 *    arquivo nenhum — mas se a foto sair do site do Wix, some daqui também.
 *
 * 2. Fotos novas: coloque os arquivos em `apps/web/public/fotos/` e escreva
 *    só o caminho, por exemplo "/fotos/fachada.jpg". Ficam junto do sistema
 *    e não dependem de mais ninguém.
 *
 * O texto do `alt` não é enfeite: é o que a pessoa cega ouve e o que o Google
 * lê. Descreva a cena, não escreva "foto".
 */
const FOTOS = {
  capa: {
    src: "/fotos/capa-playground.jpg",
    alt: "Menino sorrindo pendurado no brinquedo do parque do COC Unidade Sul",
    legenda: "fachada ou pátio com alunos — horizontal, boa luz",
  },
  a: {
    src: "/fotos/sala-de-aula.jpg",
    alt: "Aluna do Ensino Médio conversando em sala de aula",
    legenda: "sala de aula em atividade",
  },
  b: {
    src: "/fotos/bilingue-franca.jpg",
    alt: "Dois alunos segurando a bandeira da França no pátio da escola",
    legenda: "espaço bilíngue / Cambridge",
  },
  c: {
    src: "/fotos/esporte-tenis.jpg",
    alt: "Quatro crianças com raquetes de tênis em frente ao parque da escola",
    legenda: "quadra, parque ou horta",
  },
};

interface Apoio {
  cursos: Array<{ id: string; nome: string }>;
  series: Array<{ id: string; nome: string }>;
}
interface Agenda {
  dias: Array<{ dia: string; horarios: string[] }>;
  telefone: string;
  /** Código do país do WhatsApp. */
  pais: string;
  antecedenciaHoras: number;
}

const ORIGENS = [
  "Indicação de amigo ou familiar",
  "Instagram",
  "Facebook",
  "Google",
  "Placa ou fachada",
  "Já sou família COC",
  "Outro",
];

const DIAS_SEMANA = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** "2026-08-14" → { semana: "sexta", dia: "14", mes: "ago" } sem passar pelo fuso. */
function pedacos(iso: string) {
  const [a, m, d] = iso.split("-").map(Number);
  const semana = new Date(Date.UTC(a, m - 1, d)).getUTCDay();
  return { semana: DIAS_SEMANA[semana], dia: String(d).padStart(2, "0"), mes: MESES[m - 1] };
}

/** A foto quando há endereço; o espaço reservado enquanto não há. */
function Foto({ altura, foto }: { altura: number; foto: { src: string; alt: string; legenda: string } }) {
  if (foto.src) {
    return (
      <img
        src={foto.src}
        alt={foto.alt}
        loading="lazy"
        // `cover` recorta pelo centro em vez de espremer a imagem: a foto pode
        // ter qualquer proporção que o bloco continua com a mesma altura
        style={{ width: "100%", height: altura, objectFit: "cover", borderRadius: 14, display: "block" }}
      />
    );
  }
  return (
    <div
      style={{
        height: altura,
        borderRadius: 14,
        background: "repeating-linear-gradient(45deg,#eef2f0,#eef2f0 12px,#e6ebe8 12px,#e6ebe8 24px)",
        border: "1px dashed #c6d3cc",
        display: "grid",
        placeItems: "center",
        color: "#7b8b83",
        fontSize: 12,
        textAlign: "center",
        padding: 12,
      }}
    >
      FOTO: {foto.legenda}
    </div>
  );
}

function Campo({
  rotulo, valor, onChange, tipo = "text", placeholder, obrigatorio, dica, largura = "1 1 240px", maxLength,
}: {
  rotulo: string; valor: string; onChange: (v: string) => void;
  tipo?: string; placeholder?: string; obrigatorio?: boolean; dica?: string;
  largura?: string; maxLength?: number;
}) {
  return (
    <div style={{ flex: largura, minWidth: 200 }}>
      <label className="rotulo">
        {rotulo} {obrigatorio && <span style={{ color: "#d92d20" }}>*</span>}
      </label>
      <input
        className="campo" type={tipo} value={valor} placeholder={placeholder} maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
      />
      {dica && <div style={{ fontSize: 11, color: "#667085", marginTop: 4 }}>{dica}</div>}
    </div>
  );
}

export default function Agendar() {
  const [apoio, setApoio] = useState<Apoio>({ cursos: [], series: [] });
  const [agenda, setAgenda] = useState<Agenda | null>(null);
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState<{ dia: string; hora: string } | null>(null);

  const [f, setF] = useState({
    nomeAluno: "", nascimento: "", escolaAtual: "",
    nomeResponsavel: "", email: "", telefone: "", pais: PAIS_PADRAO,
    periodo: "", ondeConheceu: "", observacoes: "", website: "",
  });
  const [dia, setDia] = useState("");
  const [hora, setHora] = useState("");

  const set = (c: keyof typeof f) => (v: string) => setF((p) => ({ ...p, [c]: v }));

  useEffect(() => {
    Promise.all([get<Apoio>("/api/publico/apoio"), get<Agenda>("/api/publico/horarios")])
      .then(([a, h]) => { setApoio(a); setAgenda(h); })
      .catch(() => setErro("Não foi possível carregar os horários. Recarregue a página ou ligue para a escola."));
  }, []);

  const nascIso = useMemo(() => dataBrParaIso(f.nascimento), [f.nascimento]);
  const sugestao = useMemo(
    () => (nascIso ? sugerirEscolaridade(nascIso, anoLetivoSugerido(new Date())) : null),
    [nascIso],
  );
  const cursoId = apoio.cursos.find((c) => c.nome === sugestao?.curso)?.id ?? null;
  const serieId = apoio.series.find((s) => s.nome === sugestao?.serie)?.id ?? null;

  const horariosDoDia = agenda?.dias.find((d) => d.dia === dia)?.horarios ?? [];

  async function enviar() {
    setErro("");
    if (!f.nomeAluno.trim() || !f.nomeResponsavel.trim()) return setErro("Preencha o nome do aluno e do responsável.");
    if (!nascIso) return setErro("Confira a data de nascimento — use dd/mm/aaaa.");
    if (!f.email.includes("@")) return setErro("Confira o e-mail.");
    if (!telefoneValido(f.telefone, f.pais)) {
      return setErro(
        ehBrasil(f.pais)
          ? "Confira o telefone com DDD — celular tem 11 dígitos, fixo tem 10."
          : "Confira o número de telefone.",
      );
    }
    if (!dia || !hora) return setErro("Escolha o dia e o horário da visita.");

    setEnviando(true);
    try {
      await post("/api/publico/agendar", {
        nomeAluno: f.nomeAluno, dataNascimento: nascIso, escolaAtual: f.escolaAtual,
        nomeResponsavel: f.nomeResponsavel, emailResponsavel: f.email,
        telefoneResponsavel: soDigitos(f.telefone),
        paisTelefone: f.pais,
        cursoId, serieId, periodo: f.periodo,
        ondeConheceu: f.ondeConheceu, observacoes: f.observacoes,
        dia, hora, website: f.website,
      });
      setPronto({ dia, hora });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível concluir. Tente novamente.");
      // o horário pode ter sido tomado enquanto ela preenchia — recarrega a grade
      get<Agenda>("/api/publico/horarios").then(setAgenda).catch(() => {});
      setHora("");
    } finally {
      setEnviando(false);
    }
  }

  if (pronto) {
    const p = pedacos(pronto.dia);
    return (
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "64px 20px", textAlign: "center" }}>
        <img src={LOGO} alt="COC" style={{ width: 74, marginBottom: 20 }} />
        <h1 style={{ color: VERDE, fontSize: 26, margin: "0 0 12px" }}>Visita agendada!</h1>
        <p style={{ fontSize: 16, color: "#344054", lineHeight: 1.6 }}>
          Esperamos você <strong>{p.semana}, {p.dia} de {p.mes}, às {pronto.hora}</strong>,
          no Colégio COCSJC — Unidade Sul.
        </p>
        <p style={{ fontSize: 14, color: "#667085", marginTop: 16 }}>
          Acabamos de enviar a confirmação por WhatsApp para o telefone que você informou.
          Se precisar remarcar, é só responder aquela mensagem.
        </p>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <header style={{ borderBottom: "1px solid #eaecf0", padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, background: "#fff", zIndex: 5 }}>
        <img src={LOGO} alt="COC" style={{ width: 40 }} />
        <div>
          <div style={{ fontWeight: 700, color: VERDE, fontSize: 16 }}>Colégio COCSJC — Unidade Sul</div>
          <div style={{ fontSize: 11, color: VERDE_ESCURO, fontStyle: "italic" }}>Sua melhor opção em BILÍNGUE</div>
        </div>
      </header>

      <div style={{ maxWidth: 980, margin: "0 auto", padding: "28px 20px 64px" }}>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center", marginBottom: 32 }}>
          <div style={{ flex: "1 1 320px" }}>
            <h1 style={{ fontSize: 32, lineHeight: 1.15, color: VERDE, margin: "0 0 14px" }}>
              Venha conhecer a escola<br />antes de decidir
            </h1>
            <p style={{ fontSize: 16, color: "#475467", lineHeight: 1.65, margin: 0 }}>
              Uma hora com a nossa equipe, andando pela escola, conversando sobre o seu filho e
              vendo as salas de verdade. Escolha o melhor dia e horário — a confirmação chega
              na hora, pelo WhatsApp.
            </p>
          </div>
          <div style={{ flex: "1 1 340px" }}>
            <Foto altura={240} foto={FOTOS.capa} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 36 }}>
          <Foto altura={130} foto={FOTOS.a} />
          <Foto altura={130} foto={FOTOS.b} />
          <Foto altura={130} foto={FOTOS.c} />
        </div>

        <div className="cartao" style={{ padding: "24px 22px" }}>
          <h2 style={{ fontSize: 19, color: VERDE, margin: "0 0 4px" }}>Agende sua visita</h2>
          <p style={{ fontSize: 13, color: "#667085", margin: "0 0 22px" }}>
            Leva dois minutos. Os campos com <span style={{ color: "#d92d20" }}>*</span> são obrigatórios.
          </p>

          <div style={{ fontSize: 12, fontWeight: 700, color: VERDE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            O aluno
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 22 }}>
            <Campo rotulo="Nome completo do aluno" obrigatorio valor={f.nomeAluno} onChange={set("nomeAluno")} largura="2 1 320px" />
            <Campo rotulo="Data de nascimento" obrigatorio valor={f.nascimento} placeholder="dd/mm/aaaa" maxLength={10}
              onChange={(v) => set("nascimento")(mascaraData(v))}
              dica={sugestao ? `Turma prevista para 2027: ${sugestao.serie} — ${sugestao.curso}` : undefined} />
            <Campo rotulo="Escola atual e série" valor={f.escolaAtual} onChange={set("escolaAtual")}
              placeholder="Se já estuda em outra escola" largura="1 1 260px" />
            <div style={{ flex: "1 1 200px", minWidth: 180 }}>
              <label className="rotulo">Período pretendido</label>
              <select className="campo" value={f.periodo} onChange={(e) => set("periodo")(e.target.value)}>
                <option value="">Ainda não sei</option>
                {Object.entries(ROTULO_PERIODO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: VERDE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            Quem vem à visita
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 22 }}>
            <Campo rotulo="Seu nome" obrigatorio valor={f.nomeResponsavel} onChange={set("nomeResponsavel")} largura="2 1 300px" />
            {/* País antes do número: é ele que define a máscara e a validação. */}
            <div style={{ flex: "1 1 150px", minWidth: 140 }}>
              <label className="rotulo">País</label>
              <select className="campo" value={f.pais}
                onChange={(e) => setF((prev) => ({
                  ...prev,
                  pais: e.target.value,
                  telefone: mascaraPorPais(prev.telefone, e.target.value),
                }))}>
                {PAISES.map((p) => <option key={p.sigla} value={p.codigo}>+{p.codigo} {p.nome}</option>)}
              </select>
            </div>
            <Campo rotulo="WhatsApp" obrigatorio valor={f.telefone}
              placeholder={ehBrasil(f.pais) ? "(12) 99999-9999" : "número sem o código do país"}
              onChange={(v) => set("telefone")(mascaraPorPais(v, f.pais))}
              dica="É por aqui que a confirmação chega" />
            <Campo rotulo="E-mail" obrigatorio tipo="email" valor={f.email} onChange={set("email")} largura="1 1 260px" />
            <div style={{ flex: "1 1 240px", minWidth: 200 }}>
              <label className="rotulo">Como conheceu o COC?</label>
              <select className="campo" value={f.ondeConheceu} onChange={(e) => set("ondeConheceu")(e.target.value)}>
                <option value="">Prefiro não dizer</option>
                {ORIGENS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: VERDE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            Dia e horário
          </div>

          {!agenda ? (
            <div style={{ fontSize: 13, color: "#667085" }}>Carregando horários...</div>
          ) : agenda.dias.length === 0 ? (
            <div style={{ fontSize: 13, color: "#b54708", background: "#fffaeb", border: "1px solid #fedf89", borderRadius: 8, padding: "10px 12px" }}>
              Não há horários abertos no momento. Ligue para {agenda.telefone} que a gente encaixa você.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 10, marginBottom: 14 }}>
                {agenda.dias.map((d) => {
                  const p = pedacos(d.dia);
                  const ativo = d.dia === dia;
                  return (
                    <button key={d.dia} type="button"
                      onClick={() => { setDia(d.dia); setHora(""); }}
                      style={{ flex: "0 0 auto", width: 78, padding: "10px 6px", borderRadius: 10, cursor: "pointer",
                        border: `1px solid ${ativo ? VERDE : "#e4e7ec"}`, background: ativo ? VERDE : "#fff",
                        color: ativo ? "#fff" : "#344054", textAlign: "center" }}>
                      <div style={{ fontSize: 10, textTransform: "uppercase", opacity: .8 }}>{p.semana.slice(0, 3)}</div>
                      <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>{p.dia}</div>
                      <div style={{ fontSize: 10, opacity: .8 }}>{p.mes}</div>
                    </button>
                  );
                })}
              </div>

              {dia && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                  {horariosDoDia.map((h) => (
                    <button key={h} type="button" onClick={() => setHora(h)}
                      style={{ padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
                        border: `1px solid ${h === hora ? VERDE : "#e4e7ec"}`,
                        background: h === hora ? VERDE : "#fff", color: h === hora ? "#fff" : "#344054" }}>
                      {h}
                    </button>
                  ))}
                </div>
              )}

              <div style={{ fontSize: 12, color: "#667085", marginTop: 10 }}>
                Atendemos de segunda a sexta. <strong>Precisa de sábado?</strong> Ligue para{" "}
                <strong style={{ color: VERDE_ESCURO }}>{agenda.telefone}</strong> que a gente combina.
              </div>
            </>
          )}

          <div style={{ marginTop: 22 }}>
            <label className="rotulo">Quer nos contar algo antes da visita?</label>
            <textarea className="campo" rows={3} style={{ resize: "vertical" }} maxLength={600}
              value={f.observacoes} onChange={(e) => set("observacoes")(e.target.value)}
              placeholder="Opcional — alguma dúvida, necessidade específica, irmão já matriculado..." />
          </div>

          {/* campo-armadilha: invisível para gente, irresistível para robô */}
          <div style={{ position: "absolute", left: -9999, width: 1, height: 1, overflow: "hidden" }} aria-hidden="true">
            <label>Não preencha este campo
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website")(e.target.value)} />
            </label>
          </div>

          {erro && <div className="erro" style={{ marginTop: 16 }}>{erro}</div>}

          <button className="botao" onClick={enviar} disabled={enviando}
            style={{ marginTop: 20, width: "100%", padding: "14px", fontSize: 15 }}>
            {enviando ? "Agendando..." : "Confirmar minha visita"}
          </button>

          <p style={{ fontSize: 11, color: "#98a2b3", marginTop: 14, textAlign: "center", lineHeight: 1.6 }}>
            Usamos seus dados apenas para organizar a visita e falar com você sobre a matrícula.
            Não compartilhamos com ninguém.
          </p>
        </div>
      </div>
    </div>
  );
}
